import type {
  ApiFundMetrics,
  Fund,
  FundMetrics,
  FundSummary,
} from '@/types/fund';
import type { ConvertibleInstrumentFundDetail } from '@/types/captable';
import { toNumber, toNumberOrNull } from '@/utils/number-operations';
import { mapConvertibleInstrument } from './capTableMapper';

export function mapFundMetrics(raw: ApiFundMetrics): FundMetrics {
  return {
    fund_id: raw.fund_id,
    fund_name: raw.fund_name,
    currency: raw.currency,
    view: raw.view,

    total_commitments: toNumber(raw.total_commitments),
    total_called: toNumber(raw.total_called),
    uncalled_capital: toNumber(raw.uncalled_capital),
    total_contributed: toNumber(raw.total_contributed),
    total_distributed: toNumber(raw.total_distributed),
    nav: toNumber(raw.nav),
    gross_asset_value: toNumber(raw.gross_asset_value),

    dpi: toNumberOrNull(raw.dpi),
    tvpi: toNumberOrNull(raw.tvpi),
    rvpi: toNumberOrNull(raw.rvpi),
    gross_moic: toNumberOrNull(raw.gross_moic),

    net_irr: toNumberOrNull(raw.net_irr),

    called_capital_percentage: toNumberOrNull(raw.called_capital_percentage),
    working_capital: toNumber(raw.working_capital),

    unrealized_gain_loss: toNumber(raw.unrealized_gain_loss),
    realized_gain_loss: toNumber(raw.realized_gain_loss),

    invested_cost: toNumber(raw.invested_cost),
    invested_capital: toNumber(raw.invested_capital),
    notes_outstanding: toNumber(raw.notes_outstanding),
    cash_balance: toNumber(raw.cash_balance),

    concentration_limit: toNumberOrNull(raw.concentration_limit),
    loss_ratio: toNumberOrNull(raw.loss_ratio),
    graduation_rate: toNumberOrNull(raw.graduation_rate),
    initial_capital: toNumber(raw.initial_capital),
    follow_on_capital: toNumber(raw.follow_on_capital),
    follow_on_ratio: toNumberOrNull(raw.follow_on_ratio),

    gp_commitment_percentage: toNumberOrNull(raw.gp_commitment_percentage),
    gp_commitment_amount: toNumberOrNull(raw.gp_commitment_amount),

    management_fee_rate: toNumberOrNull(raw.management_fee_rate),
    management_fee_basis: raw.management_fee_basis,
    accrued_management_fees: toNumber(raw.accrued_management_fees),
    paid_management_fees: toNumber(raw.paid_management_fees),
    has_active_fee_schedule: raw.has_active_fee_schedule,
    fund_expenses: toNumber(raw.fund_expenses),
    deal_costs_expensed: toNumber(raw.deal_costs_expensed),

    portfolio_company_count: raw.portfolio_company_count,
    active_company_count: raw.active_company_count,
    exited_company_count: raw.exited_company_count,
  };
}

export function mapFund(raw: Fund): Fund {
  return {
    ...raw,
    commitment_amount: toNumber(raw.commitment_amount),
    minimum_prepayment_pool:
      raw.minimum_prepayment_pool != null
        ? Number(raw.minimum_prepayment_pool)
        : undefined,
    target_size: raw.target_size != null ? Number(raw.target_size) : undefined,
    hard_cap: raw.hard_cap != null ? Number(raw.hard_cap) : undefined,
    management_fee_investment_period:
      raw.management_fee_investment_period != null
        ? Number(raw.management_fee_investment_period)
        : undefined,
    management_fee_post_investment:
      raw.management_fee_post_investment != null
        ? Number(raw.management_fee_post_investment)
        : undefined,
    gp_commitment:
      raw.gp_commitment != null ? Number(raw.gp_commitment) : undefined,
    hurdle_rate: raw.hurdle_rate != null ? Number(raw.hurdle_rate) : undefined,
    gp_catch_up: raw.gp_catch_up != null ? Number(raw.gp_catch_up) : undefined,
    carried_interest:
      raw.carried_interest != null ? Number(raw.carried_interest) : undefined,
    fund_costs_cap:
      raw.fund_costs_cap != null ? Number(raw.fund_costs_cap) : undefined,
  };
}

export function mapFundSummary(raw: FundSummary): FundSummary {
  return {
    ...raw,
    commitment_amount: toNumber(raw.commitment_amount),
  };
}

export function mapConvertibleInstrumentFundDetail(
  raw: ConvertibleInstrumentFundDetail
): ConvertibleInstrumentFundDetail {
  return {
    ...mapConvertibleInstrument(raw),
    portfolio_company_name: raw.portfolio_company_name,
  };
}
